import { useState, useEffect } from 'react';
import { ArrowLeft, ArrowUp, ArrowDown, Save } from 'lucide-react';
import { databaseService } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';

const PROJECTS_COLLECTION = 'projects';

export default function SerialManagement({ onBack }) {
  const [allProjects, setAllProjects] = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const result = await databaseService.listDocuments(PROJECTS_COLLECTION);
      if (result.success) {
        const projects = result.data.documents;
        
        // Sort all projects by serialNumber
        const sortedAll = projects
          .map(project => ({
            ...project,
            serialNumber: project.serialNumber || 999999
          }))
          .sort((a, b) => a.serialNumber - b.serialNumber);
        
        // Sort featured projects by featuredSerialNumber
        const sortedFeatured = projects
          .filter(project => project.featured === true)
          .map(project => ({
            ...project,
            featuredSerialNumber: project.featuredSerialNumber || 999999
          }))
          .sort((a, b) => a.featuredSerialNumber - b.featuredSerialNumber);
        
        setAllProjects(sortedAll);
        setFeaturedProjects(sortedFeatured);
      }
    } catch (error) {
      showToast('Failed to load projects', 'error');
    } finally {
      setLoading(false);
    }
  };

  const moveUp = (list, setList, index) => {
    if (index === 0) return;
    const newList = [...list];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setList(newList);
  };

  const moveDown = (list, setList, index) => {
    if (index === list.length - 1) return;
    const newList = [...list];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    setList(newList);
  };

  const saveAllProjectsOrder = async () => {
    setSaving(true);
    try {
      // Update serialNumber for each project
      for (let i = 0; i < allProjects.length; i++) {
        await databaseService.updateDocument(
          PROJECTS_COLLECTION,
          allProjects[i].$id,
          { serialNumber: i + 1 }
        );
      }
      showToast('All projects order saved successfully', 'success');
    } catch (error) {
      showToast('Failed to save order', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveFeaturedProjectsOrder = async () => {
    setSaving(true);
    try {
      // Update featuredSerialNumber for each featured project
      for (let i = 0; i < featuredProjects.length; i++) {
        await databaseService.updateDocument(
          PROJECTS_COLLECTION,
          featuredProjects[i].$id,
          { featuredSerialNumber: i + 1 }
        );
      }
      showToast('Featured projects order saved successfully', 'success');
    } catch (error) {
      showToast('Failed to save order', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p>Loading projects...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-6 h-6" style={{ color: '#4C1D95' }} />
        </button>
        <h1 className="text-3xl font-bold" style={{ color: '#4C1D95' }}>
          Serial Management
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* All Projects Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold" style={{ color: '#4C1D95' }}>
              All Projects Order ({allProjects.length})
            </h2>
            <button
              onClick={saveAllProjectsOrder}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all duration-300 hover:scale-105 disabled:opacity-50"
              style={{ backgroundColor: '#4C1D95' }}
            >
              <Save className="w-4 h-4" />
              Save Order
            </button>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            This order will be reflected on the /projects page
          </p>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {allProjects.map((project, index) => (
              <div
                key={project.$id}
                className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => moveUp(allProjects, setAllProjects, index)}
                    disabled={index === 0}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveDown(allProjects, setAllProjects, index)}
                    disabled={index === allProjects.length - 1}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="flex items-center gap-3 flex-1">
                  <span className="text-lg font-bold text-gray-400 min-w-[30px]">
                    {index + 1}
                  </span>
                  <img
                    src={project.thumbnailUrl}
                    alt={project.title}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold line-clamp-1">{project.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className="text-xs px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: '#4C1D95' }}
                      >
                        {project.category}
                      </span>
                      {project.featured && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-400 text-yellow-900">
                          Featured
                        </span>
                      )}
                      {project.published && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-600 text-white">
                          Published
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Featured Projects Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold" style={{ color: '#4C1D95' }}>
              Featured Projects Order ({featuredProjects.length})
            </h2>
            <button
              onClick={saveFeaturedProjectsOrder}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all duration-300 hover:scale-105 disabled:opacity-50"
              style={{ backgroundColor: '#4C1D95' }}
            >
              <Save className="w-4 h-4" />
              Save Order
            </button>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            This order will be reflected on the homepage
          </p>

          {featuredProjects.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No featured projects yet. Mark projects as featured to manage their order here.
            </p>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {featuredProjects.map((project, index) => (
                <div
                  key={project.$id}
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveUp(featuredProjects, setFeaturedProjects, index)}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveDown(featuredProjects, setFeaturedProjects, index)}
                      disabled={index === featuredProjects.length - 1}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-lg font-bold text-gray-400 min-w-[30px]">
                      {index + 1}
                    </span>
                    <img
                      src={project.thumbnailUrl}
                      alt={project.title}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h3 className="font-semibold line-clamp-1">{project.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className="text-xs px-2 py-0.5 rounded-full text-white"
                          style={{ backgroundColor: '#4C1D95' }}
                        >
                          {project.category}
                        </span>
                        {project.published && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-600 text-white">
                            Published
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
